import { useCallback, useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid/non-secure';
import { callOpenAiAssistant, getAssistantConfigStatus } from './openaiClient';
import type {
    AssistantAction,
    AssistantChatMessage,
    AssistantConfigStatus,
    AssistantResponsePayload
} from './types';
import { useGraph, useSimulationStore } from '../../store/simulationStore';
import type { SimulationNode } from '../../models/simulation';
import type { SimulationState } from '../../store/simulationStore';
import { ASSISTANT_MAX_TOKENS } from '../../config/constants';
import { useSubscription } from '../subscription/SubscriptionContext';
import { supabase } from '../../lib/supabaseClient';
import { isDimensionlessUnit, normalizeUnit } from '../../utils/units';
import { useViewStore } from '../../store/viewStore';
import { useScenarioStore } from '../../store/scenarioStore';
import { buildWorkspaceAssistantContext } from './workspaceContext';

interface GraphAssistantMessage {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    actions?: AssistantAction[];
    warnings?: string[];
}

interface ApplyActionsResult {
    summaries: string[];
    warnings: string[];
}

const SYSTEM_PROMPT = `
You are a workspace assistant for a Monte Carlo simulation model composed of **distribution** and **formula** nodes. Any node can be marked as an output node using the \`isOutput\` flag.

You can answer broad questions about the current model, inputs, outputs, scenarios, results, and tornado sensitivity context. Only create graph-edit actions when the user clearly asks you to change the model. Do not change scenarios, run settings, saved versions, billing, or account state.

User input will provide the current workspace context as JSON. Always respond with a single JSON object in the following structure:
{
  "summary": string,
  "actions": Action[]
}

Possible Action types:
'create_node', 'update_node', 'delete_node', 'load_graph'

Strict requirements:

1. For informational questions, explain the answer in "summary" and return "actions": [].
2. For ambiguous requests, ask concise clarifying questions in "summary" and return "actions": [].
3. Always use node **names** as provided; never fabricate or alter IDs.
4. **Node name matching**: When referencing existing nodes in formulas, use the EXACT node name as it appears in the provided workspace context (case-sensitive, character-for-character match). Do NOT create variations - if a node is named "Number of users", reference it as "Number of users", NOT "users" or "user". Scan the workspace context to find the exact name before referencing.
5. Ensure each distribution config only contains attributes valid for its specified kind:
   - fixed: { "kind": "fixed", "value": number, "label"?: string, "description"?: string }
   - normal: { "kind": "normal", "mean": number, "std": number, "min"?: number, "max"?: number, "label"?: string, "description"?: string }
   - triangular: { "kind": "triangular", "min": number, "mode": number, "max": number, "label"?: string, "description"?: string }
6. To mark a node as an output, set \`isOutput: true\` and optionally specify a \`statistic\` field ('mean', 'median', 'p10', 'p90', 'std', or 'value').
7. Include "actions": [] if no update is necessary.
8. Keep the "summary" to two sentences or fewer OR, if the current model or user input is missing necessary information to proceed, use the summary to ask concise, clear questions to the user about what is missing. If additional information is required, do not produce any actions and only use the summary to request clarification or needed data from the user until all required details are provided, at which point proceed with JSON updates as usual.
9. **Always include units for distribution nodes**: When creating distribution nodes, always specify an appropriate \`unit\` field unless the value is dimensionless (ratios, percentages, counts). Use contextually appropriate units (e.g., prices → "EUR/t" or "USD/kg", yields → "kg/ha", areas → "ha"). Always use the singular form of the unit (e.g., "unit" not "units").
10. **Make at most 5 consecutive changes to the graph**: If you need to make more than 5 changes, stop and ask the user if you should continue with additional changes. Explain in the summary what you will do next.

Formula language constraints:
- Use only **single-line** JavaScript expressions.
- Reference nodes as \`\${"Exact Node Name"}\`; inner double quotes must be escaped (\`"\`). **There must be NO whitespace between \`\${\` and \`"\`**. Correct: \`\${"Variable Name"}\`. Incorrect: \`\${ "Variable Name"}\`.
- **The node name inside \`\${"..."}\` must match EXACTLY** (case-sensitive, character-for-character) the node name as it appears in the provided workspace context. Before writing any formula, scan the workspace context to find the exact node name. Do NOT use abbreviations, singular/plural variations, or shortened names (e.g., if a node is named "Number of users", use \`\${"Number of users"}\`, NOT \`\${"users"}\` or \`\${"user"}\`).
- Allowed operators: +, -, *, /, %, **, (, ), <, <=, >, >=, ==, !=.
- Only \`math\` object from math.js library is allowed, with: abs, ceil, floor, round, min, max, pow, sqrt, log, exp. Use \`math.max()\`, \`math.min()\`, etc. (not \`Math.max()\`). The math.js library properly handles units, unlike JavaScript's native Math object.
- Disallowed: function definitions, template literals, backticks, newlines, semicolons, or using commas outside function calls, or any global variables other than \`math\`.
- **Disallowed: if-else statements and ternary operators (\`? :\`)**. Division by zero will raise an error naturally - do not guard against it.
- **Variable reference format**: Use \`\${"Variable Name"}\` with NO whitespace between \`\${\` and \`"\`. Incorrect: \`\${ "Variable Name"}\`. Correct: \`\${"Variable Name"}\`.
- Never reference nonexistent nodes; create them in the same response if needed.

Safety and consistency rules:
- When converting units, create clear intermediate nodes (e.g., kg 2t), not repeated constants in formulas.
- If changing a distribution kind, remove any config fields that aren't valid for the new type.
- Do not rename nodes except when the user explicitly requests it or to resolve an exact duplicate; if you do, also update all dependent expressions accordingly.
- For metrics needing unavailable inputs, create placeholder **distribution** nodes with \`fixed\` kind, an explicit \`value\`, and a clear label; connect these to formulas.
- **Units are strongly recommended** for all distribution nodes to enable dimensional validation. Always include a \`unit\` field when creating distribution nodes unless the value is truly dimensionless (e.g., ratios, percentages, multipliers, factors). **Do NOT create units for dimensionless values** - if a value is a ratio, percentage, multiplier, or factor, omit the \`unit\` field entirely. Only distribution nodes have a \`unit\` field. Formula node units are automatically derived from their inputs.
- **Tags**: Nodes can have optional \`tags\` (array of strings) for categorization. Tags are normalized to lowercase. Use tags to organize nodes for financial analysis (e.g., "revenue", "cost", "profit", "variable-cost", "fixed-cost", "primary-revenue", "secondary-revenue"). When creating or updating nodes, suggest appropriate tags based on the node's purpose (e.g., revenue nodes should have "revenue" tag, cost nodes should have "cost" tag). Tags help organize nodes in comparison views and P&L statements.
- **Unit selection guidelines**: Choose units that match the context (e.g., prices should use "EUR/t" or "USD/kg", yields should use "kg/ha" or "t/ha", areas should use "ha" or "acre"). Use compound units for rates (e.g., "EUR/t", "kg/ha", "EUR/km").
- **Available base units**: Mass (kg, t, g, lb, oz), Distance (m, km, cm, mm, mi, ft, in), Area (ha, acre, m^2, km^2), Volume (L, mL, m^3, gal), Currency (EUR, USD, GBP), Time (s, min, h, day, week, month, year), Energy (J, kWh), Power (W, kW). Compound units are supported (e.g., "kg/ha", "EUR/t", "m^2").
- **Custom units**: In addition to base units, you can create custom units for domain-specific quantities (e.g., "user", "subscription", "customer", "session"). Custom units are automatically created when you specify them in the \`unit\` field. They must start with a letter and contain only letters, numbers, and underscores. The current simulation's custom units are provided in the graph JSON as \`customUnits\`.
- **Currency units**: EUR, USD, and GBP are treated as separate, incompatible units. They cannot be mixed in operations (e.g., EUR + USD will fail). If currency conversion is needed, create explicit exchange rate nodes and multiply in formulas.
- **Unit simplification**: Units are automatically simplified when possible (e.g., "EUR * g / EUR" becomes "g"). You can include unit expressions directly in formulas if needed.


Error-handling:

- For ambiguous instructions, make only the minimum needed additions/changes and explain briefly in the summary. If you cannot proceed due to missing information, ask the user for the required details in the summary and wait for additional input before taking further action.
- If a suggested formula is unsafe, substitute a guarded version that maintains user intent.

Core assumption rule:
- Do not make core assumptions yourself; for any ambiguity, missing information, or alternatives, always explicitly ask the user for clarification before proceeding.
`;

const FORMULA_PROMPT = `
Developer: You are an assistant that helps edit formula expressions in Monte Carlo simulation nodes.

User input will provide the current formula expression and available nodes. Always respond with a single JSON object:
{
  "summary": string,
  "expression": string
}

Formula language constraints:
- Use only **single-line** JavaScript expressions.
- Reference nodes as \`\${"Exact Node Name"}\`; inner double quotes must be escaped (\`"\`). **There must be NO whitespace between \`\${\` and \`"\`**. Correct: \`\${"Variable Name"}\`. Incorrect: \`\${ "Variable Name"}\`.
- **The node name inside \`\${"..."}\` MUST match EXACTLY** (case-sensitive, character-for-character) the node name from the "Available nodes to reference" list provided in the user input. Do NOT use abbreviations, singular/plural variations, or shortened names. Always copy the exact name from the provided list (e.g., if the list shows "Number of users", use \`\${"Number of users"}\`, NOT \`\${"users"}\` or \`\${"user"}\`).
- Allowed operators: +, -, *, /, %, **, (, ), &&, ||, !, <, <=, >, >=, ==, !=.
- Only \`math\` object from math.js library is allowed, with: abs, ceil, floor, round, min, max, pow, sqrt, log, exp. Use \`math.max()\`, \`math.min()\`, etc. (not \`Math.max()\`). The math.js library properly handles units, unlike JavaScript's native Math object.
- Disallowed: function definitions, template literals, backticks, newlines, semicolons, or using commas outside function calls, or any global variables other than \`math\`.
- **Disallowed: if-else statements and ternary operators (\`? :\`)**. Division by zero will raise an error naturally - do not guard against it.
- **Variable reference format**: Use \`\${"Variable Name"}\` with NO whitespace between \`\${\` and \`"\`. Incorrect: \`\${ "Variable Name"}\`. Correct: \`\${"Variable Name"}\`.
- Only reference nodes that exist in the provided list. Verify each node name matches exactly before using it.
- **Unit awareness**: Formulas can include unit expressions directly (e.g., "5 kg", "value as kg/ha"). Units in formulas are automatically simplified. The math.js library properly handles units in functions like \`math.max()\` and \`math.min()\`, preserving units when all operands have compatible units. Be aware of unit compatibility when combining values - the system will validate that operations make dimensional sense (e.g., EUR + USD will fail, but EUR/t * t will work). Custom units (like "user", "subscription") can also be used in formulas and will be automatically created if they don't exist yet. Always use the singular form of the unit (e.g., "user" not "users").

Safety rules:
- **Before writing any formula, carefully review the "Available nodes to reference" list and copy the exact node name** (including capitalization, spacing, and punctuation) when referencing it. Do not create variations.
- When converting units, use clear intermediate calculations or include unit expressions in formulas (e.g., "value * 1000 kg / t" to convert tonnes to kg). The system will automatically simplify unit expressions.
- Keep the "summary" to one sentence.
- Output only the JSON object; add no extra text, comments, or markdown.

Examples (use as reference for spirit, not as output):
- Rate clipping: \`"expression": "math.min(math.max(\${"Crop failure rate"}, 0), 1)"\`
- **Correct node reference**: If the available nodes list shows "Number of users", use \`\${"Number of users"}\` (exact match, no whitespace after \${).
- ** Incorrect node reference **: Do NOT use \`\${"users"}\` or \`\${"user"}\` if the actual node name is "Number of users". Do NOT use \`\${ "Number of users"}\` (with space after \${).
`;

const describeUpdate = (
    type: string,
    beforeName: string,
    afterName: string,
    changeList: string[]
) => {
    const renameSegment = beforeName !== afterName ? `renamed to "${afterName}"` : null;
    const details = [...(renameSegment ? [renameSegment] : []), ...changeList];

    if (!details.length) {
        return `No changes applied to ${type} node "${afterName}"`;
    }

    return `Updated ${type} node "${afterName}": ${details.join(', ')} `;
};

const describeConfigChanges = (config?: Record<string, unknown>) => {
    if (!config) return undefined;
    const keys = Object.keys(config);
    if (!keys.length) return undefined;
    return `config fields ${keys.join(', ')} updated`;
};

const applyAssistantActions = (actions: AssistantAction[]): ApplyActionsResult => {
    const summaries: string[] = [];
    const warnings: string[] = [];
    const store: SimulationState = useSimulationStore.getState();
    const isViewer = store.currentAccessLevel === 'viewer';

    actions.forEach((action) => {
        switch (action.type) {
            case 'create_node': {
                // Prevent creating nodes if user is viewer
                if (isViewer) {
                    warnings.push(
                        `Cannot create node "${action.node?.name ?? 'unknown'}": Viewers cannot add new nodes to simulations. You can only modify existing nodes.`
                    );
                    break;
                }
                try {
                    // Normalize tags if present
                    if (action.node.tags && Array.isArray(action.node.tags)) {
                        const normalizedTags = action.node.tags
                            .map((tag) => tag.toLowerCase().trim())
                            .filter((tag) => tag.length > 0);
                        action.node.tags = normalizedTags.length > 0 ? normalizedTags : undefined;
                    }

                    // Check if this is a distribution node with a unit
                    if (action.node.type === 'distribution' && action.node.unit) {
                        const unitName = action.node.unit.trim();

                        // Check if unit is dimensionless - if so, remove it
                        if (isDimensionlessUnit(unitName)) {
                            warnings.push(`Removed dimensionless unit "${unitName}" from distribution node "${action.node.name}".Dimensionless values(ratios, percentages, multipliers) should not have units.`);
                            // Remove the unit from the node before creating
                            const nodeWithoutUnit = { ...action.node };
                            delete (nodeWithoutUnit as any).unit;
                            const created = store.addNode(nodeWithoutUnit, action.position);
                            summaries.push(`Created ${created.data.type} node "${created.data.name}"`);
                        } else {
                            // Auto-register the unit if it doesn't exist
                            const currentCustomUnits = store.graph.customUnits || [];
                            if (!currentCustomUnits.includes(unitName)) {
                                // Extract base unit identifiers from compound units
                                const unitIdentifiers = unitName.split(/[*/^()\s]+/).filter(id => id.trim().length > 0);

                                // Try to register each identifier as a custom unit
                                for (const identifier of unitIdentifiers) {
                                    const trimmed = identifier.trim();
                                    // Only register if it's not already a built-in unit and passes validation
                                    if (trimmed && !currentCustomUnits.includes(trimmed)) {
                                        const result = store.addCustomUnit(trimmed);
                                        if (result.success) {
                                            // Unit was registered successfully
                                        } else if (result.error && !result.error.includes('conflicts')) {
                                            // Only warn if it's not a conflict (conflicts are expected for built-in units)
                                            warnings.push(`Could not register unit "${trimmed}": ${result.error} `);
                                        }
                                    }
                                }
                            }

                            const created = store.addNode(action.node, action.position);
                            summaries.push(`Created ${created.data.type} node "${created.data.name}"`);
                        }
                    } else {
                        const created = store.addNode(action.node, action.position);
                        summaries.push(`Created ${created.data.type} node "${created.data.name}"`);
                    }
                } catch (err) {
                    warnings.push(
                        `Failed to create node "${action.node?.name ?? 'unknown'}": ${(err as Error).message} `
                    );
                }
                break;
            }
            case 'update_node': {
                const targetName = action.target?.name;
                if (!targetName) {
                    warnings.push('update_node action missing target.name');
                    break;
                }

                const current = store.graph.nodes.find(
                    (node: SimulationNode) => node.data.name === targetName
                );
                if (!current) {
                    warnings.push(`Node "${targetName}" not found for update.`);
                    break;
                }

                const nodeId = current.id;
                const type = current.data.type;
                const appliedDetails: string[] = [];
                const changes = {
                    ...action.changes,
                    config: action.changes.config ? { ...action.changes.config } : undefined
                };

                if (changes.config && type !== 'distribution') {
                    warnings.push(`Cannot update config on ${type} node "${targetName}".`);
                    delete changes.config;
                }

                if (changes.expression && type !== 'formula') {
                    warnings.push(`Cannot update expression on ${type} node "${targetName}".`);
                    delete changes.expression;
                }

                // Handle unit updates for distribution nodes
                if (type === 'distribution' && 'unit' in changes && changes.unit !== undefined) {
                    const unitName = typeof changes.unit === 'string' ? normalizeUnit(changes.unit.trim()) : undefined;

                    if (unitName) {
                        // Check if unit is dimensionless - if so, remove it
                        if (isDimensionlessUnit(unitName)) {
                            warnings.push(`Removed dimensionless unit "${unitName}" from distribution node "${targetName}".Dimensionless values(ratios, percentages, multipliers) should not have units.`);
                            delete changes.unit;
                        } else {
                            // Auto-register the unit if it doesn't exist
                            const currentCustomUnits = store.graph.customUnits || [];
                            if (!currentCustomUnits.includes(unitName)) {
                                // Extract base unit identifiers from compound units
                                const unitIdentifiers = unitName.split(/[*/^()\s]+/).filter(id => id.trim().length > 0);

                                // Try to register each identifier as a custom unit
                                for (const identifier of unitIdentifiers) {
                                    const trimmed = identifier.trim();
                                    // Only register if it's not already a custom unit and passes validation
                                    if (trimmed && !currentCustomUnits.includes(trimmed)) {
                                        const result = store.addCustomUnit(trimmed);
                                        if (result.success) {
                                            // Unit was registered successfully
                                        } else if (result.error && !result.error.includes('conflicts')) {
                                            // Only warn if it's not a conflict (conflicts are expected for built-in units)
                                            warnings.push(`Could not register unit "${trimmed}": ${result.error} `);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // isOutput and statistic can be set on any node type

                const beforeName = current.data.name;

                store.updateNode(nodeId, (draft: SimulationNode) => {
                    if (typeof changes.name === 'string' && changes.name.trim()) {
                        if (changes.name.trim() !== draft.data.name) {
                            draft.data.name = changes.name.trim();
                        }
                    }

                    if (typeof changes.description === 'string') {
                        if ((draft.data.description ?? '') !== changes.description) {
                            draft.data.description = changes.description;
                            appliedDetails.push('description updated');
                        }
                    }

                    if (changes.expression !== undefined && draft.data.type === 'formula') {
                        if (draft.data.expression !== changes.expression) {
                            draft.data.expression = changes.expression;
                            appliedDetails.push('expression updated');
                        }
                    }

                    if (draft.data.type === 'distribution' && changes.config) {
                        draft.data.config = {
                            ...draft.data.config,
                            ...changes.config
                        } as typeof draft.data.config;
                        const configDetail = describeConfigChanges(
                            changes.config as Record<string, unknown>
                        );
                        if (configDetail) {
                            appliedDetails.push(configDetail);
                        }
                    }

                    // Handle unit updates for distribution nodes
                    if (draft.data.type === 'distribution' && 'unit' in changes) {
                        if (changes.unit === undefined || changes.unit === null) {
                            // Remove unit if explicitly set to undefined/null
                            (draft.data as any).unit = undefined;
                            appliedDetails.push('unit removed');
                        } else if (typeof changes.unit === 'string' && changes.unit.trim()) {
                            const newUnit = normalizeUnit(changes.unit.trim());
                            const currentUnit = (draft.data as any).unit;
                            if (currentUnit !== newUnit) {
                                (draft.data as any).unit = newUnit;
                                appliedDetails.push(`unit set to ${newUnit} `);
                            }
                        }
                    }

                    if (changes.isOutput !== undefined) {
                        const data = draft.data as any;
                        data.isOutput = changes.isOutput;
                        if (changes.isOutput) {
                            appliedDetails.push('marked as output');
                        } else {
                            appliedDetails.push('unmarked as output');
                        }
                    }

                    if (changes.statistic !== undefined) {
                        const data = draft.data as any;
                        data.statistic = changes.statistic;
                        appliedDetails.push(`statistic set to ${changes.statistic} `);
                    }

                    if (changes.tags !== undefined) {
                        const data = draft.data as any;
                        // Normalize tags to lowercase
                        const normalizedTags = Array.isArray(changes.tags)
                            ? changes.tags.map((tag) => tag.toLowerCase().trim()).filter((tag) => tag.length > 0)
                            : [];
                        data.tags = normalizedTags.length > 0 ? normalizedTags : undefined;
                        if (normalizedTags.length > 0) {
                            appliedDetails.push(`tags set to [${normalizedTags.join(', ')}] `);
                        } else {
                            appliedDetails.push('tags removed');
                        }
                    }
                });

                const updated = useSimulationStore
                    .getState()
                    .graph.nodes.find((node: SimulationNode) => node.id === nodeId);
                const afterName = updated?.data.name ?? beforeName;

                summaries.push(describeUpdate(type, beforeName, afterName, appliedDetails));
                break;
            }
            case 'delete_node': {
                const targetName = action.target?.name;
                if (!targetName) {
                    warnings.push('delete_node action missing target.name');
                    break;
                }

                const target = store.graph.nodes.find(
                    (node: SimulationNode) => node.data.name === targetName
                );
                if (!target) {
                    warnings.push(`Node "${targetName}" not found for deletion.`);
                    break;
                }

                store.removeNode(target.id);
                summaries.push(`Deleted ${target.data.type} node "${target.data.name}"`);
                break;
            }
            case 'load_graph': {
                // Prevent loading/replacing entire graph if user is viewer
                if (isViewer) {
                    warnings.push(
                        `Cannot replace graph: Viewers cannot replace the entire simulation. You can only modify existing nodes.`
                    );
                    break;
                }
                try {
                    store.loadGraph(action.graph);
                    summaries.push(
                        `Replaced graph with "${action.graph.name}"(${action.graph.nodes.length} nodes)`
                    );
                } catch (err) {
                    warnings.push(`Failed to load graph "${action.graph?.name ?? 'unknown'}": ${(err as Error).message} `);
                }
                break;
            }
            default:
                warnings.push(`Unsupported action type: ${(action as AssistantAction).type} `);
        }
    });

    return { summaries, warnings };
};

interface SavedInteractionMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp?: string;
}

interface SavedInteraction {
    id: string;
    messages: SavedInteractionMessage[];
    created_at: string;
}

export const useWorkspaceAssistant = () => {
    const graph = useGraph();
    const activeView = useViewStore((state) => state.activeView);
    const runConfig = useSimulationStore((state) => state.runConfig);
    const lastResult = useSimulationStore((state) => state.lastResult);
    const selectedNodeId = useSimulationStore((state) => state.selectedNodeId);
    const currentSimulationName = useSimulationStore((state) => state.currentSimulationName);
    const currentVersionId = useSimulationStore((state) => state.currentVersionId);
    const currentVersionName = useSimulationStore((state) => state.currentVersionName);
    const scenarios = useScenarioStore((state) => state.scenarios);
    const activeScenarioId = useScenarioStore((state) => state.activeScenarioId);
    const scenarioResults = useScenarioStore((state) => state.scenarioResults);
    const [messages, setMessages] = useState<GraphAssistantMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [configStatus, setConfigStatus] = useState<AssistantConfigStatus>(getAssistantConfigStatus());
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const { canUseAssistant, remainingAssistantRequests, refreshSubscription } = useSubscription();
    const currentSimulationId = useSimulationStore((state) => state.currentSimulationId);

    const historyRef = useRef<AssistantChatMessage[]>([]);

    // Load saved interactions when simulation changes
    useEffect(() => {
        const loadSavedInteractions = async () => {
            if (!currentSimulationId) {
                // Clear messages when no simulation is loaded
                setMessages([]);
                historyRef.current = [];
                return;
            }

            try {
                const { data, error: fetchError } = await supabase
                    .from('assistant_interactions')
                    .select('id, messages, created_at')
                    .eq('simulation_id', currentSimulationId)
                    .order('created_at', { ascending: true });

                if (fetchError) {
                    console.error('Error loading saved interactions:', fetchError);
                    return;
                }

                if (!data || data.length === 0) {
                    setMessages([]);
                    historyRef.current = [];
                    return;
                }

                // Get the most recent interaction which contains the full conversation
                const latestInteraction = data[data.length - 1] as SavedInteraction;

                // Reconstruct messages from the latest interaction
                // Messages are now sanitized (no system messages, clean user/assistant text)
                const allMessages: GraphAssistantMessage[] = [];
                const allHistory: AssistantChatMessage[] = [];

                latestInteraction.messages.forEach((msg: SavedInteractionMessage) => {
                    // Messages are already sanitized - no system messages stored
                    // User messages contain only the request text
                    // Assistant messages contain only the summary
                    if (msg.role === 'user' || msg.role === 'assistant') {
                        const messageId = nanoid();
                        const displayText = msg.content; // Already clean

                        allMessages.push({
                            id: messageId,
                            role: msg.role,
                            text: displayText,
                        });

                        // Store clean messages in history (system prompt will be added when sending)
                        allHistory.push({ role: msg.role, content: msg.content });
                    }
                });

                setMessages(allMessages);
                historyRef.current = allHistory;
            } catch (err) {
                console.error('Error loading saved interactions:', err);
            }
        };

        loadSavedInteractions();
    }, [currentSimulationId]);

    const sendMessage = useCallback(
        async (text: string) => {
            const trimmed = text.trim();
            if (!trimmed || isLoading) {
                return;
            }

            const status = getAssistantConfigStatus();
            setConfigStatus(status);

            // Check if user can use assistant (subscription limits)
            if (!canUseAssistant()) {
                // Show upgrade modal when limit is reached
                setShowUpgradeModal(true);
                return;
            }

            setMessages((prev) => [
                ...prev,
                {
                    id: nanoid(),
                    role: 'user',
                    text: trimmed
                }
            ]);

            if (!status.isConfigured) {
                const warning = `Assistant not configured.Missing env vars: ${status.missing.join(', ')} `;
                setError(warning);
                setMessages((prev) => [
                    ...prev,
                    {
                        id: nanoid(),
                        role: 'assistant',
                        text: warning,
                        warnings: [warning]
                    }
                ]);
                return;
            }

            const workspaceContext = buildWorkspaceAssistantContext({
                activeView,
                graph,
                runConfig,
                lastResult,
                currentSimulationId,
                currentSimulationName,
                currentVersionId,
                currentVersionName,
                selectedNodeId,
                scenarios,
                activeScenarioId,
                scenarioResults
            });
            const workspaceContextJson = JSON.stringify(workspaceContext, null, 2);
            const conversation: AssistantChatMessage[] = [
                { role: 'system', content: SYSTEM_PROMPT },
                ...historyRef.current,
                {
                    role: 'user',
                    content: `User request: \n${trimmed} \n\nCurrent workspace context as JSON: \n${workspaceContextJson} `
                }
            ];

            setIsLoading(true);
            setError(null);

            try {
                const response = await callOpenAiAssistant<AssistantResponsePayload>({
                    messages: conversation,
                    maxOutputTokens: ASSISTANT_MAX_TOKENS,
                    simulationId: currentSimulationId,
                    graphSnapshot: graph
                });

                let payload = response.payload;

                // Fallback: if payload is missing, try to parse it from content
                if (!payload && response.content) {
                    try {
                        const parsed = JSON.parse(response.content);
                        payload = parsed as AssistantResponsePayload;
                    } catch (parseError) {
                        console.error('Failed to parse assistant content as JSON:', parseError);
                    }
                }

                if (!payload) {
                    throw new Error(`Assistant response missing payload. Content: ${response.content?.substring(0, 200)}`);
                }

                const actions = payload.actions ?? [];
                if (!Array.isArray(actions)) {
                    throw new Error(`Assistant response actions is not an array. Received: ${typeof payload.actions}. Payload keys: ${Object.keys(payload).join(', ')}`);
                }

                if (typeof payload.summary !== 'string') {
                    throw new Error(`Assistant response summary is not a string. Received: ${typeof payload.summary}. Payload keys: ${Object.keys(payload).join(', ')}`);
                }

                const { summaries, warnings: actionWarnings } = applyAssistantActions(actions);
                const warnings = [...actionWarnings];

                // Auto-tidy layout after applying changes
                if (summaries.length > 0) {
                    try {
                        await useSimulationStore.getState().tidyLayout();
                    } catch (err) {
                        // Tidy layout errors are already logged, don't fail the assistant
                    }
                }

                // Only show the summary text, nothing else
                const assistantText = payload.summary.trim() || 'No summary provided.';

                setMessages((prev) => [
                    ...prev,
                    {
                        id: nanoid(),
                        role: 'assistant',
                        text: assistantText,
                        actions,
                        warnings
                    }
                ]);

                historyRef.current.push({ role: 'user', content: trimmed });
                historyRef.current.push({ role: 'assistant', content: payload.summary });

                // Refresh subscription to update assistant request count
                await refreshSubscription();
            } catch (err) {
                const message = (err as Error).message;
                setError(message);
                setMessages((prev) => [
                    ...prev,
                    {
                        id: nanoid(),
                        role: 'assistant',
                        text: `Assistant error: ${message} `,
                        warnings: [message]
                    }
                ]);
            } finally {
                setIsLoading(false);
            }
        },
        [
            activeScenarioId,
            activeView,
            graph,
            isLoading,
            currentSimulationId,
            currentSimulationName,
            currentVersionId,
            currentVersionName,
            lastResult,
            refreshSubscription,
            runConfig,
            scenarioResults,
            scenarios,
            selectedNodeId,
            canUseAssistant
        ]
    );

    const reset = useCallback(() => {
        historyRef.current = [];
        setMessages([]);
        setError(null);
    }, []);

    return {
        messages,
        isLoading,
        error,
        sendMessage,
        reset,
        configStatus,
        remainingRequests: remainingAssistantRequests(),
        canUse: canUseAssistant(),
        showUpgradeModal,
        setShowUpgradeModal
    } as const;
};

export const useGraphAssistant = useWorkspaceAssistant;

interface FormulaUpdatePayload {
    summary: string;
    expression: string;
}

export const useFormulaAssistant = () => {
    const graph = useGraph();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [configStatus] = useState<AssistantConfigStatus>(getAssistantConfigStatus());

    const updateFormula = useCallback(
        async (nodeId: string, currentExpression: string, userPrompt: string): Promise<{ expression: string; summary: string } | null> => {
            if (!userPrompt.trim() || isLoading) {
                return null;
            }

            const status = getAssistantConfigStatus();
            if (!status.isConfigured) {
                setError(`Assistant not configured.Missing env vars: ${status.missing.join(', ')} `);
                return null;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Get available nodes for reference
                const availableNodes = graph.nodes
                    .filter((node) => node.id !== nodeId)
                    .map((node) => ({ id: node.id, name: node.data.name, type: node.data.type }));

                const conversation: AssistantChatMessage[] = [
                    { role: 'system', content: FORMULA_PROMPT },
                    {
                        role: 'user',
                        content: `Current expression: ${currentExpression || '(empty)'} \n\nAvailable nodes to reference: \n${availableNodes.map(n => `- ${n.name} (${n.type})`).join('\n')} \n\nUser request: ${userPrompt.trim()} `
                    }
                ];

                const response = await callOpenAiAssistant<FormulaUpdatePayload>({
                    messages: conversation,
                    maxOutputTokens: ASSISTANT_MAX_TOKENS
                });

                const payload = response.payload;

                if (!payload || typeof payload.expression !== 'string' || typeof payload.summary !== 'string') {
                    throw new Error('Assistant response missing expression or summary.');
                }

                // Update the node's expression
                useSimulationStore.getState().updateNode(nodeId, (node) => {
                    if (node.data.type === 'formula') {
                        node.data.expression = payload.expression;
                    }
                });

                return { expression: payload.expression, summary: payload.summary };
            } catch (err) {
                const message = (err as Error).message;
                setError(message);
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [graph, isLoading]
    );

    return {
        isLoading,
        error,
        updateFormula,
        configStatus,
        resetError: () => setError(null)
    } as const;
};
